import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { RoleCode } from '../../api/contrat.js';
import { COULEURS, POLICE_EQUIPE, styleBouton } from '../../app/styles.js';

/**
 * Couloir G, point 5 (décision de Joel du 30/10, journal/decisions.md) : « jamais bloqué ». Quand une condition manque, l'action
 * ouvre cet avertissement au lieu d'être refusée. La dirigeante (ou l'administration) peut forcer, avec un motif obligatoire ;
 * l'assistante lit « Seule Isabelle peut débloquer cette étape » et peut la prévenir (une tâche lui est créée). Le forçage est tracé
 * par le serveur (qui, quand, motif, conditions manquantes) ; rien n'est écrit tant que l'on n'a pas confirmé.
 */
export const MOTIF_FORCAGE_MIN = 3;
export const MOTIF_FORCAGE_MAX = 300;

export const peutForcer = (role: RoleCode | null): boolean => role === 'dirigeante' || role === 'admin';

export interface PropsAvertissement {
  /** Libellés des conditions qui manquent (« Mandat signé au dossier »). */
  conditions: readonly string[];
  role: RoleCode | null;
  /** Prénom de la dirigeante, lu dans la donnée du catalogue (« Isabelle »). */
  dirigeante: string;
  /** Enregistre le forçage ; vrai si le serveur l'a accepté (l'avertissement se referme alors). */
  forcer: (motif: string) => Promise<boolean>;
  /** Crée la demande de déblocage ; vrai si elle est enregistrée. */
  prevenir: () => Promise<boolean>;
  annuler: () => void;
  /** Boutons qui permettent de remplir les conditions tout de suite (ex. « Envoyer le mandat »). */
  remplir?: ReactNode;
}

const voile: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(30, 33, 60, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 50,
};
const boite: CSSProperties = {
  background: COULEURS.blanc,
  color: COULEURS.marineFonce,
  fontFamily: POLICE_EQUIPE,
  fontSize: 16,
  lineHeight: 1.5,
  borderRadius: 16,
  border: `2px solid ${COULEURS.ambre}`,
  padding: 20,
  maxWidth: 560,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  boxSizing: 'border-box',
};
const gros: CSSProperties = { ...styleBouton, minHeight: 48, padding: '0 20px', fontSize: 16 };
const principal: CSSProperties = { ...gros, border: `2px solid ${COULEURS.marine}`, background: COULEURS.marine, color: COULEURS.blanc, fontWeight: 600 };
const rangee: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' };
const ECHEC = 'L’enregistrement n’a pas abouti. Vérifiez la connexion puis réessayez.';

export function AvertissementForcage({ conditions, role, dirigeante, forcer, prevenir, annuler, remplir }: PropsAvertissement) {
  const titre = useId();
  const champ = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [prevenue, setPrevenue] = useState(false);
  const autorisee = peutForcer(role);
  useEffect(() => ref.current?.focus(), []);
  useEffect(() => {
    const echap = (e: KeyboardEvent) => e.key === 'Escape' && annuler();
    globalThis.addEventListener?.('keydown', echap);
    return () => globalThis.removeEventListener?.('keydown', echap);
  }, [annuler]);

  const lancer = async (action: () => Promise<boolean>, apres: () => void) => {
    setEnCours(true);
    setErreur(null);
    const ok = await action().catch(() => false);
    setEnCours(false);
    if (ok) apres();
    else setErreur(ECHEC);
  };
  const motifPret = motif.trim().length >= MOTIF_FORCAGE_MIN;

  return (
    <div style={voile}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titre} tabIndex={-1} data-avertissement-forcage="" style={boite}>
        <h2 id={titre} style={{ margin: 0, fontSize: 20, color: COULEURS.ambre }}>
          Attention
        </h2>
        <p style={{ margin: 0 }}>Attention : nous vous déconseillons de continuer parce que ceci manque encore :</p>
        <ul style={{ margin: 0, paddingLeft: 20, fontWeight: 600 }}>
          {conditions.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {remplir ? <div style={rangee}>{remplir}</div> : null}
        {autorisee ? (
          <>
            <p style={{ margin: 0 }}>Vous pouvez forcer : votre nom, la date et le motif seront gardés au journal du dossier.</p>
            <label htmlFor={champ} style={{ fontWeight: 600 }}>
              Motif du forçage (obligatoire), par exemple « Mandat signé sur papier »
            </label>
            <textarea
              id={champ}
              value={motif}
              maxLength={MOTIF_FORCAGE_MAX}
              rows={2}
              onChange={(e) => setMotif(e.target.value)}
              style={{ font: 'inherit', fontSize: 16, padding: 8, borderRadius: 12, border: `1px solid ${COULEURS.bordure}`, width: '100%', boxSizing: 'border-box' }}
            />
            <div style={rangee}>
              <button type="button" style={principal} disabled={!motifPret || enCours} onClick={() => void lancer(() => forcer(motif.trim()), () => undefined)}>
                Forcer et continuer
              </button>
              <button type="button" style={gros} onClick={annuler}>
                Annuler
              </button>
            </div>
          </>
        ) : prevenue ? (
          <>
            <p role="status" style={{ margin: 0, fontWeight: 600 }}>
              {dirigeante} est prévenue : une tâche lui a été créée pour débloquer cette étape.
            </p>
            <div style={rangee}>
              <button type="button" style={gros} onClick={annuler}>
                Fermer
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontWeight: 600 }}>Seule {dirigeante} peut débloquer cette étape. Vous pouvez la prévenir.</p>
            <div style={rangee}>
              <button type="button" style={principal} disabled={enCours} onClick={() => void lancer(prevenir, () => setPrevenue(true))}>
                Prévenir {dirigeante}
              </button>
              <button type="button" style={gros} onClick={annuler}>
                Annuler
              </button>
            </div>
          </>
        )}
        {erreur ? (
          <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
            {erreur}
          </p>
        ) : null}
      </div>
    </div>
  );
}
